import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { CheckCircle, AlertCircle, ChevronRight, ChevronLeft, Loader2 } from "lucide-react";
import { API } from "../config/api";
import { LOGO_URL } from "../config/constants";

const INVOLVED_FACTORS = [
  "Linked transactions", "Islamic finance", "Shared Ownership", "New build",
  "Additional bare land / grounds (Not a garden)", "Trusts", "Wills / inheritance", "Divorce", "None"
];

const FTB_EXCLUSIONS = [
  "Inherited property", "Interests in properties via trusts",
  "Interests in properties via usufructs", "Interests in foreign properties",
  "A spouse who has interests in other residential properties",
  "Purchasing jointly with a spouse who is not a first-time buyer",
  "Joint purchase with someone who will not be using the property as their main residence",
  "Purchasing a property to be used for any purpose other than as their main residence",
  "None"
];

const LOCATIONS = ["England", "Wales", "Northern Ireland", "Scotland"];
const ENTITY_TYPES = ["Individual or Couple", "Limited Company", "Charity", "Council", "Trust", "Partnership"];

function RadioGroup({ name, options, value, onChange, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-3">
      {options.map(opt => (
        <label key={opt} className={`flex items-center gap-2 px-4 py-2.5 rounded-md border cursor-pointer transition-all text-sm ${value === opt ? 'border-teal bg-teal-dim text-teal font-medium' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`}>
          <input type="radio" name={name} value={opt} checked={value === opt} onChange={() => onChange(opt)}
            className="sr-only" data-testid={`${testPrefix}-${opt.toLowerCase().replace(/\s+/g,'-')}`} />
          <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center ${value === opt ? 'border-teal' : 'border-gray-300'}`}>
            {value === opt && <div className="w-2 h-2 rounded-md bg-teal" />}
          </div>
          {opt}
        </label>
      ))}
    </div>
  );
}

function CheckboxGroup({ options, selected, onChange, testPrefix }) {
  const toggle = (opt) => {
    if (opt === "None") {
      onChange(selected.includes("None") ? [] : ["None"]);
    } else {
      const without = selected.filter(s => s !== "None" && s !== opt);
      onChange(selected.includes(opt) ? without : [...without, opt]);
    }
  };
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map(opt => (
        <label key={opt} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md border cursor-pointer transition-all text-sm ${selected.includes(opt) ? 'border-teal bg-teal-dim text-teal' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`}>
          <Checkbox checked={selected.includes(opt)} onCheckedChange={() => toggle(opt)}
            data-testid={`${testPrefix}-${opt.toLowerCase().replace(/\s+/g,'-').substring(0,30)}`} />
          <span className="leading-tight">{opt}</span>
        </label>
      ))}
    </div>
  );
}

function FieldBlock({ label, children, required }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-gray-700">
        {label} {required && <span className="text-status-red">*</span>}
      </Label>
      {children}
    </div>
  );
}

export default function SDLTForm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formMeta, setFormMeta] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const topRef = useRef(null);

  const [f, setF] = useState({
    property_address: "", purchaser_1: "", purchaser_2: "", purchaser_3: "", purchaser_4: "",
    additional_purchasers: "", transaction_type: "", tenure_type: "", lease_type: "",
    lease_premium: "", involved_factors: [], property_location: "", purchaser_entity_type: "",
    is_probate: "", is_chain_rescue: "", previously_owned: "", ftb_exclusions: [],
    owns_two_plus: "", owns_with_others: "", other_properties_location: "",
    other_properties_description: "", replacing_main_residence: "", all_uk_residents: "",
    property_classification: "", has_hazards: "", has_additional_dwellings: "",
    number_of_dwellings: "", intend_rent_redevelop: "", expected_price: "",
    borrowing_parties_changing: "", equity_split_changing: "",
    additional_info: "", declaration_name: "", declaration_date: "",
  });

  const set = (key, val) => setF(prev => ({ ...prev, [key]: val }));
  const setMulti = (key, val) => setF(prev => ({ ...prev, [key]: val }));

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/sdlt/form/${token}`);
        if (!res.ok) throw new Error("Invalid link");
        const data = await res.json();
        if (data.status === "completed") {
          setSubmitted(true);
          setFormMeta(data);
        } else {
          setFormMeta(data);
          if (data.property_address) set("property_address", data.property_address);
        }
      } catch (e) {
        setError("This questionnaire link is invalid or has expired. Please contact your solicitor for a new link.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth" });

  const isPurchase = f.transaction_type === "Purchase";
  const isRemortgage = f.transaction_type === "Remortgage / Transfer of Equity";

  // Steps: 0=Intro/Type, 1=Purchase/Remortgage details, 2=Ownership (purchase only), 3=Property (purchase only), 4=Declaration
  const getSteps = () => {
    const steps = [{ id: "intro", label: "Transaction" }];
    if (isPurchase) {
      steps.push({ id: "purchase", label: "Purchase Details" });
      steps.push({ id: "ownership", label: "Ownership" });
      steps.push({ id: "property", label: "Property" });
    } else if (isRemortgage) {
      steps.push({ id: "remortgage", label: "Remortgage" });
    }
    steps.push({ id: "declaration", label: "Declaration" });
    return steps;
  };

  const steps = getSteps();
  const isLast = step === steps.length - 1;
  const canNext = () => {
    if (step === 0) return f.transaction_type && f.purchaser_1 && f.property_address;
    if (steps[step]?.id === "declaration") return f.declaration_name && f.declaration_date;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/sdlt/submit/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          responses: f,
          declaration_name: f.declaration_name,
          declaration_date: f.declaration_date,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-teal" />
    </div>
  );

  if (error && !formMeta) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md bg-white rounded-md shadow-sm border p-8 text-center" data-testid="sdlt-error">
        <AlertCircle className="w-12 h-12 text-status-red mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Link Invalid</h2>
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md bg-white rounded-md shadow-sm border p-8 text-center" data-testid="sdlt-submitted">
        <CheckCircle className="w-14 h-14 text-teal mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Thank You</h2>
        <p className="text-gray-500 text-sm">Your SDLT / LTT Questionnaire has been submitted successfully. Your solicitor will review the information provided.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" ref={topRef}>
      {/* Header */}
      <div className="glass-nav sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src={LOGO_URL} alt="Bonsai Law" className="h-10 w-auto" />
          <div>
            <h1 className="text-base font-semibold text-gray-900" data-testid="sdlt-title">SDLT / LTT Questionnaire</h1>
            {formMeta?.client_name && <p className="text-xs text-gray-500">{formMeta.client_name}</p>}
          </div>
        </div>
        {/* Progress */}
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <div className="flex gap-1">
            {steps.map((s, i) => (
              <div key={s.id} className="flex-1">
                <div className={`h-1 rounded-md ${i <= step ? 'bg-teal' : 'bg-gray-200'}`} />
                <p className={`text-[10px] mt-1 ${i <= step ? 'text-teal font-medium' : 'text-gray-400'}`}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Body */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-3 bg-status-red-dim border border-red-200 rounded-md text-sm text-status-red" data-testid="sdlt-form-error">
            {error}
            <button onClick={() => setError("")} className="ml-2 underline">Dismiss</button>
          </div>
        )}

        <div className="bg-white rounded-md shadow-sm border p-6 space-y-6">

          {/* Step 0: Transaction Type */}
          {steps[step]?.id === "intro" && (
            <>
              <div className="pb-4 border-b">
                <h2 className="text-base font-semibold text-gray-900">Transaction Information</h2>
                <p className="text-sm text-gray-500 mt-1">Please provide accurate and complete answers. Any misleading or false information could result in fines or penalties.</p>
              </div>

              <FieldBlock label="Address of Property being purchased" required>
                <Textarea value={f.property_address} onChange={e => set("property_address", e.target.value)}
                  placeholder="Enter full property address" data-testid="sdlt-property-address" />
              </FieldBlock>

              <div className="grid gap-4 sm:grid-cols-2">
                {[1,2,3,4].map(i => (
                  <FieldBlock key={i} label={`Purchaser ${i}${i === 1 ? '' : ' (optional)'}`} required={i === 1}>
                    <Input value={f[`purchaser_${i}`]} onChange={e => set(`purchaser_${i}`, e.target.value)}
                      placeholder={`Full name`} data-testid={`sdlt-purchaser-${i}`} />
                  </FieldBlock>
                ))}
              </div>

              <FieldBlock label="Is the transaction a purchase or remortgage?" required>
                <RadioGroup name="transaction_type"
                  options={["Purchase", "Remortgage / Transfer of Equity"]}
                  value={f.transaction_type} onChange={v => set("transaction_type", v)} testPrefix="sdlt-tx" />
              </FieldBlock>
            </>
          )}

          {/* Step 1a: Purchase Details */}
          {steps[step]?.id === "purchase" && (
            <>
              <div className="pb-4 border-b">
                <h2 className="text-base font-semibold text-gray-900">Purchase Details</h2>
              </div>

              <FieldBlock label="Is this transaction freehold or leasehold?">
                <RadioGroup name="tenure_type" options={["Freehold", "Leasehold"]}
                  value={f.tenure_type} onChange={v => set("tenure_type", v)} testPrefix="sdlt-tenure" />
              </FieldBlock>

              {f.tenure_type === "Leasehold" && (
                <>
                  <FieldBlock label="Does the transaction involve the grant of a new lease or assignment of an existing lease?">
                    <RadioGroup name="lease_type" options={["Grant", "Existing", "Other"]}
                      value={f.lease_type} onChange={v => set("lease_type", v)} testPrefix="sdlt-lease" />
                  </FieldBlock>
                  <FieldBlock label="What is the Lease Premium?">
                    <Input value={f.lease_premium} onChange={e => set("lease_premium", e.target.value)}
                      placeholder="e.g. 250,000" data-testid="sdlt-lease-premium" />
                  </FieldBlock>
                </>
              )}

              <FieldBlock label="Are any of the following involved in this purchase?">
                <CheckboxGroup options={INVOLVED_FACTORS} selected={f.involved_factors}
                  onChange={v => setMulti("involved_factors", v)} testPrefix="sdlt-factors" />
              </FieldBlock>

              <FieldBlock label="Where is the property being purchased located?">
                <RadioGroup name="property_location" options={LOCATIONS}
                  value={f.property_location} onChange={v => set("property_location", v)} testPrefix="sdlt-location" />
              </FieldBlock>

              <FieldBlock label="What form of entity is the purchaser using?">
                <RadioGroup name="purchaser_entity_type" options={ENTITY_TYPES}
                  value={f.purchaser_entity_type} onChange={v => set("purchaser_entity_type", v)} testPrefix="sdlt-entity" />
              </FieldBlock>

              <FieldBlock label="Is this a purchase of a Probate Property?">
                <RadioGroup name="is_probate" options={["Yes", "No"]}
                  value={f.is_probate} onChange={v => set("is_probate", v)} testPrefix="sdlt-probate" />
              </FieldBlock>

              <FieldBlock label="Is this purchase saving a chain from collapsing?">
                <RadioGroup name="is_chain_rescue" options={["Yes", "No"]}
                  value={f.is_chain_rescue} onChange={v => set("is_chain_rescue", v)} testPrefix="sdlt-chain" />
              </FieldBlock>
            </>
          )}

          {/* Step 1b: Remortgage */}
          {steps[step]?.id === "remortgage" && (
            <>
              <div className="pb-4 border-b">
                <h2 className="text-base font-semibold text-gray-900">Remortgage / Transfer of Equity</h2>
              </div>

              <FieldBlock label="Are the borrowing parties to the mortgage changing?">
                <RadioGroup name="borrowing_parties_changing" options={["Yes", "No"]}
                  value={f.borrowing_parties_changing} onChange={v => set("borrowing_parties_changing", v)} testPrefix="sdlt-borrowing" />
              </FieldBlock>

              {f.borrowing_parties_changing === "No" && (
                <FieldBlock label="Is the equity split for the parties to the mortgage changing?">
                  <RadioGroup name="equity_split_changing" options={["Yes", "No"]}
                    value={f.equity_split_changing} onChange={v => set("equity_split_changing", v)} testPrefix="sdlt-equity" />
                </FieldBlock>
              )}
            </>
          )}

          {/* Step 2: Ownership & Residency (Purchase only) */}
          {steps[step]?.id === "ownership" && (
            <>
              <div className="pb-4 border-b">
                <h2 className="text-base font-semibold text-gray-900">Ownership & Residency</h2>
              </div>

              <FieldBlock label="Have any of the purchasers ever owned residential property? (Including inherited, overseas, holiday homes, etc.)">
                <RadioGroup name="previously_owned" options={["Yes", "No"]}
                  value={f.previously_owned} onChange={v => set("previously_owned", v)} testPrefix="sdlt-prevowned" />
              </FieldBlock>

              {f.previously_owned === "No" && (
                <FieldBlock label="Does any purchaser meet any of the following criteria which could exclude the purchase from First-Time-Buyer relief?">
                  <CheckboxGroup options={FTB_EXCLUSIONS} selected={f.ftb_exclusions}
                    onChange={v => setMulti("ftb_exclusions", v)} testPrefix="sdlt-ftb" />
                </FieldBlock>
              )}

              {f.previously_owned === "Yes" && (
                <FieldBlock label="Will completion of the transaction result in any purchaser owning two or more residential properties on the day of completion?">
                  <RadioGroup name="owns_two_plus" options={["Yes", "No"]}
                    value={f.owns_two_plus} onChange={v => set("owns_two_plus", v)} testPrefix="sdlt-twoplus" />
                </FieldBlock>
              )}

              <FieldBlock label="Do you own or part-own any property with someone else? (Including through inheritance or any other scenario)">
                <RadioGroup name="owns_with_others" options={["Yes", "No"]}
                  value={f.owns_with_others} onChange={v => set("owns_with_others", v)} testPrefix="sdlt-ownsothers" />
              </FieldBlock>

              {f.owns_with_others === "Yes" && (
                <>
                  <FieldBlock label="Where are these properties located?">
                    <RadioGroup name="other_properties_location" options={["UK", "Europe", "Rest of the world"]}
                      value={f.other_properties_location} onChange={v => set("other_properties_location", v)} testPrefix="sdlt-otherloc" />
                  </FieldBlock>
                  <FieldBlock label="Description of other properties">
                    <Textarea value={f.other_properties_description} onChange={e => set("other_properties_description", e.target.value)}
                      placeholder="Please describe..." data-testid="sdlt-other-desc" />
                  </FieldBlock>
                </>
              )}

              <FieldBlock label="Is the purchaser replacing their previous main residence with the property being purchased?">
                <RadioGroup name="replacing_main_residence" options={["Yes", "No"]}
                  value={f.replacing_main_residence} onChange={v => set("replacing_main_residence", v)} testPrefix="sdlt-replacing" />
              </FieldBlock>

              <FieldBlock label="Are ALL purchasers UK residents?">
                <RadioGroup name="all_uk_residents" options={["Yes", "No"]}
                  value={f.all_uk_residents} onChange={v => set("all_uk_residents", v)} testPrefix="sdlt-ukres" />
              </FieldBlock>
            </>
          )}

          {/* Step 3: Property Classification (Purchase only) */}
          {steps[step]?.id === "property" && (
            <>
              <div className="pb-4 border-b">
                <h2 className="text-base font-semibold text-gray-900">Property Details</h2>
              </div>

              <FieldBlock label="Is the property residential or non-residential?">
                <RadioGroup name="property_classification" options={["Residential", "Non-Residential"]}
                  value={f.property_classification} onChange={v => set("property_classification", v)} testPrefix="sdlt-class" />
              </FieldBlock>

              <FieldBlock label="Does the property have significant amounts of asbestos, hazardous materials, or severe structural issues?">
                <RadioGroup name="has_hazards" options={["Yes", "No"]}
                  value={f.has_hazards} onChange={v => set("has_hazards", v)} testPrefix="sdlt-hazards" />
              </FieldBlock>

              <FieldBlock label="Does the property have any additional dwellings? (Self-contained annexes or habitable outbuildings)">
                <RadioGroup name="has_additional_dwellings" options={["Yes", "No"]}
                  value={f.has_additional_dwellings} onChange={v => set("has_additional_dwellings", v)} testPrefix="sdlt-dwellings" />
              </FieldBlock>

              {f.has_additional_dwellings === "Yes" && (
                <FieldBlock label="How many dwellings are in the purchase?">
                  <Input type="number" min="1" value={f.number_of_dwellings}
                    onChange={e => set("number_of_dwellings", e.target.value)}
                    placeholder="e.g. 2" data-testid="sdlt-dwelling-count" />
                </FieldBlock>
              )}

              <FieldBlock label="Do you intend to either rent or redevelop the property?">
                <RadioGroup name="intend_rent_redevelop" options={["Yes", "No"]}
                  value={f.intend_rent_redevelop} onChange={v => set("intend_rent_redevelop", v)} testPrefix="sdlt-rent" />
              </FieldBlock>

              <FieldBlock label="Expected purchase price" required>
                <Input value={f.expected_price} onChange={e => set("expected_price", e.target.value)}
                  placeholder="e.g. 450,000" data-testid="sdlt-price" />
              </FieldBlock>
            </>
          )}

          {/* Declaration Step (always last) */}
          {steps[step]?.id === "declaration" && (
            <>
              <div className="pb-4 border-b">
                <h2 className="text-base font-semibold text-gray-900">Additional Information & Declaration</h2>
              </div>

              <FieldBlock label="Is there any further relevant information you would like to share?">
                <Textarea value={f.additional_info} onChange={e => set("additional_info", e.target.value)}
                  placeholder="Enter any additional information here..." rows={4} data-testid="sdlt-additional" />
              </FieldBlock>

              {f.purchaser_3 === "" && f.purchaser_4 === "" ? null : null}

              <div className="p-4 bg-status-amber-dim border border-amber-200 rounded-md">
                <p className="text-sm text-status-amber font-medium mb-2">Declaration</p>
                <p className="text-xs text-status-amber leading-relaxed">
                  I declare that the information I/we have given on this form is correct and complete to the best of our
                  knowledge and belief and understand that we may have to pay financial penalties and face prosecution
                  if I/we give false information which results in an incorrect submission being made.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <FieldBlock label="Your Full Name" required>
                  <Input value={f.declaration_name} onChange={e => set("declaration_name", e.target.value)}
                    placeholder="Full name" data-testid="sdlt-decl-name" />
                </FieldBlock>
                <FieldBlock label="Date" required>
                  <Input type="date" value={f.declaration_date} onChange={e => set("declaration_date", e.target.value)}
                    data-testid="sdlt-decl-date" />
                </FieldBlock>
              </div>

              <p className="text-xs text-gray-400 leading-relaxed">
                Please note that we are not qualified to offer any advice on non-legal matters such as the value of the
                property, the condition of the property, or any issues that may be raised by any environmental searches
                &mdash; nor can we provide specific financial investment and tax advice including Stamp Duty (SDLT/LTT).
              </p>
            </>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-6 pb-8">
          {step > 0 ? (
            <Button variant="outline" onClick={() => { setStep(step - 1); scrollTop(); }} data-testid="sdlt-back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          ) : <div />}

          {isLast ? (
            <Button onClick={handleSubmit} disabled={!canNext() || submitting}
              className="bg-teal hover:bg-teal text-white px-6" data-testid="sdlt-submit-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Submit Questionnaire"}
            </Button>
          ) : (
            <Button onClick={() => { setStep(step + 1); scrollTop(); }} disabled={!canNext()}
              className="bg-teal hover:bg-teal text-white" data-testid="sdlt-next-btn">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
